<script lang="ts">
    import type {GroupTransaction, Transaction} from './types';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';
    import {loc} from 'shared/utils/pipes';
    import {pipe} from 'core/utils';
    import {slide} from 'svelte/transition';
    import TransactionMoneyIndicator from './TransactionMoneyIndicator.svelte';
    import {getDateFormatter, getInjector, getLocalization} from 'core/svelte-context/context';
    import {KpUserFinancePanelService} from './kp-user-finance-panel.service';
    import {createEventDispatcher} from 'svelte';

    export let group: GroupTransaction;
    export let userCanDeletePayment = true;
    export let userCanDeleteDebt = true;
    export let transactions: Transaction[];

    const localize = getLocalization();
    const dateFormatter = getDateFormatter();
    const individualTransactionsSlideAnimParams = {duration: 250};
    const service = getInjector().getByToken<KpUserFinancePanelService>(KpUserFinancePanelService.serviceName);
    const dispatch = createEventDispatcher<{ 'forgive-transaction': void }>();
    let individualTransactionsShown = false;

    const handleToggleTransactionsShownClick = () => {
        individualTransactionsShown = !individualTransactionsShown;
    }

    const handleForgiveTransactionClick = async (transaction: Transaction) => {
        const successful = await service.forgive(transaction);
        if (!successful) {
            return;
        }

        group.sum -= transaction.sum;
        transaction.sum = 0;

        // Trigger reactivity
        group = group;
        transactions = transactions;

        dispatch('forgive-transaction');
    }

    function isTransactionForgivable(transaction: Transaction) {
        if (transaction.sum === 0) {
            return false;
        }

        if (transaction.debt && userCanDeleteDebt) {
            return true;
        }

        return transaction.payment && userCanDeletePayment;
    }
</script>

<div class="kp-transaction-group-item-container">
    <div class="transaction-group-item">
        <KpButton buttonSize="sm" on:click={handleToggleTransactionsShownClick}>
            <UIcon icon="{individualTransactionsShown ? 'zoom-out' : 'zoom-in'}"
                   label="{individualTransactionsShown ? localize(/* @kp-localization konto.HideTransactionsLabel */ 'konto.HideTransactionsLabel') : localize(/* @kp-localization konto.ShowTransactionsLabel */ 'konto.ShowTransactionsLabel')}"/>
        </KpButton>

        <span>{pipe(group.text, loc())}</span>

        <div class="money-amount-container">
            <TransactionMoneyIndicator amount="{group.sum}"/>
        </div>
    </div>

    {#if individualTransactionsShown}
        <div class="individual-transactions-container"
             transition:slide={individualTransactionsSlideAnimParams}>

            {#if transactions.length === 0}
                <div class="empty-container">
                    <span class="text-muted text-center"></span>
                </div>
            {/if}

            {#each transactions as transaction(transaction.id)}
                <div class="individual-transaction">
                    <div class="transaction-mainrow">
                        {pipe(transaction.date, dateFormatter('d.M.yyyy'))}
                        {#if transaction.cashierDepartment}
                            -
                            {pipe(transaction.cashierDepartment, loc())}
                        {/if}

                        <div class="money-amount-container">
                            <TransactionMoneyIndicator amount="{transaction.sum}"/>
                        </div>

                        {#if isTransactionForgivable(transaction)}
                            <KpButton buttonSize="sm" on:click={() => handleForgiveTransactionClick(transaction)}>
                                <UIcon icon="trash"
                                       label="{localize(/* @kp-localization konto.NullifyTransaction */ 'konto.NullifyTransaction')}"/>
                            </KpButton>
                        {/if}
                    </div>
                    {#if (transaction.sum > 0) && transaction.targetDepartment.id !== transaction.cashierDepartment.id }
                        <div class="transaction-detailrow">
                            {localize(/* @kp-localization payment.paidFor */ 'payment.paidFor')}: {pipe(transaction.targetDepartment, loc())}
                        </div>
                    {/if}

                </div>
            {/each}
        </div>
    {/if}
</div>

<style lang="less">
    @import (reference) "bootstrap-less/bootstrap/variables";

    @row-height: 40px;

    .kp-transaction-group-item-container {
        height: 100%;
        display: flex;
        flex-direction: column;

        .transaction-group-item {
            display: flex;
            align-items: center;
            gap: @padding-base-horizontal;
            padding: 0 @panel-body-padding;
            height: @row-height;
            border-bottom: 1px solid @panel-default-border;
        }

        .money-amount-container {
            font-weight: 500;
            margin-left: auto;
        }

        .individual-transactions-container {
            display: flex;
            flex-direction: column;
            background-color: @table-bg-hover;

            .individual-transaction {
                display: flex;
                flex-direction: column;
                border-bottom: 1px solid @panel-default-border;

                .transaction-mainrow {
                    display: flex;
                    align-items: center;
                    gap: @padding-base-horizontal;
                    padding: 8px @panel-body-padding 4px @panel-body-padding;
                }

                .transaction-detailrow {
                    display: flex;
                    align-items: center;
                    gap: @padding-base-horizontal;
                    padding-left: @panel-body-padding;
                    padding-bottom: 8px;
                    color: #777777;
                    font-weight: lighter;
                }
            }
        }
    }
</style>
